import { EdgeDevice, StoreEdge } from '../../core/models';

// How an Edge's state is put into words, shared by the Store Edges page and the line on a location's own
// page so a store reads the same on both.

/** Mirrors StoreEdge.OnlineWithin — two of the Edge's own 15-second beats, so one missed beat does not
 *  flicker a shop from green to amber. */
const ONLINE_WITHIN_SECONDS = 30;

/** Mirrors StoreEdge.RecentWithin. Both are here only to write the tooltips: the status is decided by the
 *  API and never recomputed in the browser, so a threshold that moves server-side changes the wording here
 *  and nothing else. */
const RECENT_WITHIN_MINUTES = 5;

export type EdgeStatusSeverity = 'success' | 'warn' | 'danger' | 'secondary';

/** One word, no seconds: an operator standing here is asking "is that store up", not "how many seconds
 *  ago". `neverseen` is kept apart from `offline` because they are different first questions — one machine
 *  was never started, the other worked and went quiet. */
const LABELS: Record<StoreEdge['status'], string> = {
    online: 'Online',
    recent: 'Recent',
    offline: 'Offline',
    neverseen: 'Never seen'
};

/** Offline is the only row that asks for a phone call, so it is the only one in the colour this app already
 *  uses for "this needs you". Never seen is grey rather than loud: nothing broke, an install never finished. */
const SEVERITIES: Record<StoreEdge['status'], EdgeStatusSeverity> = {
    online: 'success',
    recent: 'warn',
    offline: 'danger',
    neverseen: 'secondary'
};

export function edgeStatusLabel(edge: StoreEdge): string {
    return LABELS[edge.status];
}

export function edgeStatusSeverity(edge: StoreEdge): EdgeStatusSeverity {
    return SEVERITIES[edge.status];
}

/** The same words for a device that has a key but no Edge row yet. An enrolled machine and an installed one sit
 *  in one section of the admin, and a machine must not change its vocabulary the moment it is bound to a store. */
export function livenessLabel(status: StoreEdge['status']): string {
    return LABELS[status];
}

export function livenessSeverity(status: StoreEdge['status']): EdgeStatusSeverity {
    return SEVERITIES[status];
}

/** WHAT A HUMAN DECIDED, which is a different axis from whether the machine is up: an approved device is still
 *  "Never seen" until it beats, and a pending one has no liveness at all. Both badges appear on the same row, so
 *  neither word may be one the other could also use. */
const DECISIONS: Record<EdgeDevice['status'], string> = {
    pending: 'Waiting',
    approved: 'Approved',
    rejected: 'Rejected'
};

/** Waiting is the only row that asks anything of the operator, so it is the only one drawn in the colour this app
 *  uses for "this needs you". Rejected is grey rather than loud: nothing broke, somebody said no. */
const DECISION_SEVERITIES: Record<EdgeDevice['status'], EdgeStatusSeverity> = {
    pending: 'warn',
    approved: 'success',
    rejected: 'secondary'
};

export function deviceStatusLabel(device: EdgeDevice): string {
    return DECISIONS[device.status];
}

export function deviceStatusSeverity(device: EdgeDevice): EdgeStatusSeverity {
    return DECISION_SEVERITIES[device.status];
}

/** The sentence behind the badge. "Approved" carries the extra fact that decides whether orders flow, because a
 *  device can be approved and running and still serving nothing. */
export function deviceStatusTitle(device: EdgeDevice): string {
    switch (device.status) {
        case 'pending':
            return 'Waiting for someone to approve or reject it. It holds no key and can reach nothing.';
        case 'approved':
            return device.bound
                ? `Approved by ${device.decidedBy} and bound to ${device.claimedLocationId}.`
                : `Approved by ${device.decidedBy}. It has a key and can report in, but no orders reach it until `
                  + `${device.claimedLocationId} is onboarded and linked.`;
        case 'rejected':
            return `Rejected by ${device.decidedBy}. The machine has been told to stop asking.`;
    }
}

/** The sentence behind the badge, for its tooltip. The badge no longer carries seconds, so this is where the
 *  thresholds are spelled out — otherwise "Recent" is a word with no meaning attached to it. */
export function edgeStatusTitle(edge: StoreEdge): string {
    const seen = edge.lastSeenAt ? new Date(edge.lastSeenAt).toLocaleString() : null;
    switch (edge.status) {
        case 'online':
            return `Heard from within the last ${ONLINE_WITHIN_SECONDS} seconds.`;
        case 'recent':
            return `Last heard from ${seen} — quiet for over ${ONLINE_WITHIN_SECONDS} seconds but under `
                + `${RECENT_WITHIN_MINUTES} minutes, which is a shop's minute of bad internet rather than an outage.`;
        case 'offline':
            return `Nothing heard since ${seen} — an Edge is called offline after `
                + `${RECENT_WITHIN_MINUTES} minutes of silence.`;
        case 'neverseen':
            return 'Registered, but never heard from — the machine has not started its Edge yet.';
    }
}

const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 60 * SECONDS_PER_MINUTE;
const SECONDS_PER_DAY = 24 * SECONDS_PER_HOUR;

/** "4 minutes ago". A few lines rather than a date library — this is the only place that needs one, and the
 *  exact timestamp is always one hover away.
 *
 *  DELIBERATELY NO SECONDS, even though the data has them: a column that counts up second by second reads as
 *  something to watch, when the question it answers is only ever minutes-coarse. The badge says whether the
 *  store is up; this says roughly how stale that is. */
export function relativeTime(iso: string | null | undefined): string {
    if (!iso) return 'never';
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return 'never';

    const seconds = Math.round((Date.now() - then) / 1000);
    // Covers clock skew too: a store PC's clock and ours disagree by a few seconds, and a timestamp slightly
    // in the future is that skew rather than news, so it falls in here instead of reading as negative.
    if (seconds < SECONDS_PER_MINUTE) return 'less than a minute ago';
    if (seconds < SECONDS_PER_HOUR) return ago(seconds / SECONDS_PER_MINUTE, 'minute');
    if (seconds < SECONDS_PER_DAY) return ago(seconds / SECONDS_PER_HOUR, 'hour');

    const days = Math.floor(seconds / SECONDS_PER_DAY);
    return days === 1 ? 'yesterday' : `${days} days ago`;
}

function ago(value: number, unit: string): string {
    const whole = Math.floor(value);
    return `${whole} ${unit}${whole === 1 ? '' : 's'} ago`;
}
